import { useState } from 'react';
import type { FormEvent } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { TestVerdictBadge } from '@/components/StatusBadge';
import { shortDate, today } from '@/lib/format';
import { useCreateTestReport, useJudgeTestReport, useTestReports } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { QualityTestType } from '@/lib/types';

const TEST_TYPES: QualityTestType[] = ['압축강도', '배합', '슬럼프', '염화물', '철근인장', '토질밀도', '기타'];

/**
 * 시험성적서 대장.
 *
 * <p>채취일과 시험일을 따로 두는 것이 요점이다 — 압축강도는 재령 7일·28일에 시험하므로 그 사이가
 * 비어 있는 줄이 정상이고, 그 줄이 <b>28일 뒤에 누가 챙기나</b>를 묻는 자리다.
 *
 * <p>협력사가 채취·결과를 적고 <b>합부는 감리가 누른다.</b> 결과값을 적었다고 합격이 되지 않는다.
 */
export function TestReportsPage() {
  const { siteId, siteName, isJudge, isVendor } = useSite();
  const reports = useTestReports(siteId);
  const create = useCreateTestReport(siteId);
  const judge = useJudgeTestReport(siteId);

  const [open, setOpen] = useState(false);
  const [testType, setTestType] = useState<QualityTestType>('압축강도');
  const [target, setTarget] = useState('');
  const [sampledOn, setSampledOn] = useState(today);
  const [testedOn, setTestedOn] = useState('');
  const [agency, setAgency] = useState('');
  const [specValue, setSpecValue] = useState('');
  const [resultValue, setResultValue] = useState('');

  const rows = reports.data ?? [];
  // 등록은 협력사만, 합부는 판정하는 손(감리·직원)이다(DOC-87 §2-3).
  const canWrite = isVendor;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await create.mutateAsync({
      testType,
      target: target.trim(),
      sampledOn,
      testedOn: testedOn === '' ? null : testedOn,
      agency: agency.trim() === '' ? null : agency.trim(),
      specValue: specValue.trim(),
      resultValue: resultValue.trim() === '' ? null : resultValue.trim(),
    });
    setOpen(false);
    setTarget('');
    setTestedOn('');
    setSpecValue('');
    setResultValue('');
  }

  return (
    <>
      <PageHeader
        title="시험성적서"
        subtitle={siteName}
        action={
          canWrite && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              등록
            </button>
          )
        }
      />

      {open && (
        <form onSubmit={onSubmit} className="card mb-3">
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>시험종류</span>
              <select className="input-field" value={testType} onChange={(e) => setTestType(e.target.value as QualityTestType)}>
                {TEST_TYPES.map((it) => <option key={it} value={it}>{it}</option>)}
              </select>
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>시험기관</span>
              <input type="text" className="input-field" value={agency} onChange={(e) => setAgency(e.target.value)} />
            </label>
          </div>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>대상</span>
            <input type="text" className="input-field" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="B2 슬래브 24MPa" required />
          </label>
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>채취일</span>
              <input type="date" className="input-field" value={sampledOn} onChange={(e) => setSampledOn(e.target.value)} required />
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>시험일</span>
              <input type="date" className="input-field" value={testedOn} onChange={(e) => setTestedOn(e.target.value)} />
            </label>
          </div>
          <div className="mb-4 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>규격값</span>
              <input type="text" className="input-field" value={specValue} onChange={(e) => setSpecValue(e.target.value)} placeholder="24MPa 이상" required />
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>결과값</span>
              <input type="text" className="input-field" value={resultValue} onChange={(e) => setResultValue(e.target.value)} placeholder="27.4MPa" />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
              {create.isPending ? '보내는 중입니다…' : '등록'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={reports.isPending}
        error={reports.isError}
        empty={rows.length === 0}
        emptyText="등록된 성적서가 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-ui-md font-medium">
                    {row.testType} <span className="text-ui-sm font-normal">{row.target}</span>
                  </div>
                  <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                    채취 {shortDate(row.sampledOn)} · 시험 {shortDate(row.testedOn)}
                    {row.agency !== null && ` · ${row.agency}`}
                  </div>
                </div>
                <TestVerdictBadge verdict={row.verdict} />
              </div>

              <div className="flex items-end justify-between gap-3">
                <span className="text-ui-xs" style={{ color: 'var(--text-secondary)' }}>규격 {row.specValue}</span>
                <span
                  className="text-ui-lg font-semibold"
                  style={{ color: row.verdict === '불합격' ? 'var(--danger)' : 'var(--text-primary)' }}
                >
                  {row.resultValue ?? '결과 없음'}
                </span>
              </div>

              {isJudge && row.verdict === '판정대기' && row.resultValue !== null && (
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className="btn-primary flex-1"
                    onClick={() => judge.mutate({ id: row.id, verdict: '합격' })}
                    disabled={judge.isPending}
                  >
                    합격
                  </button>
                  <button
                    type="button"
                    className="btn-secondary flex-1"
                    style={{ color: 'var(--danger)' }}
                    onClick={() => judge.mutate({ id: row.id, verdict: '불합격' })}
                    disabled={judge.isPending}
                  >
                    불합격
                  </button>
                </div>
              )}

              {row.reviewedOn !== null && (
                <div className="mt-2 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {shortDate(row.reviewedOn)} {row.reviewedBy ?? ''} 판정
                </div>
              )}
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
